import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Briefcase, MapPin, Clock, Upload, X, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobCard = ({ job, onApply }) => (
  <div
    data-testid={`careers-job-${job.id}`}
    className="bg-[#0B132B]/50 border border-white/10 hover:border-red-500/50 transition-colors p-6 group"
  >
    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-slate-500">
      <Briefcase size={11} className="text-red-500" />
      {job.department}
    </div>
    <h3 className="mt-3 font-display text-white text-2xl md:text-3xl tracking-tight font-light">
      {job.title}
    </h3>
    <div className="mt-4 flex flex-wrap gap-4 text-slate-400 text-xs font-mono">
      <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {job.location}</span>
      <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {job.type}</span>
      {job.experience && (
        <span className="inline-flex items-center gap-1.5">/ {job.experience}</span>
      )}
    </div>
    <p className="mt-4 text-slate-400 text-sm line-clamp-3 leading-relaxed">
      {job.description}
    </p>
    <button
      onClick={() => onApply(job)}
      data-testid={`careers-apply-${job.id}`}
      className="mt-6 inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-medium group-hover:translate-x-0.5 transition-transform"
    >
      Apply for this role <ArrowRight size={14} />
    </button>
  </div>
);

const ApplyModal = ({ job, onClose }) => {
  const [values, setValues] = useState({
    name: "", email: "", phone: "", cover_letter: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your resume");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Resume must be ≤ 10 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("job_id", job.id);
      form.append("name", values.name);
      form.append("email", values.email);
      if (values.phone) form.append("phone", values.phone);
      if (values.cover_letter) form.append("cover_letter", values.cover_letter);
      form.append("resume", file);
      await axios.post(`${API}/careers/apply`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Application failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="careers-apply-modal"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-[#0B132B] border border-white/10 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10 flex justify-between items-start">
          <div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-red-500">
              Apply
            </div>
            <h3 className="mt-1 font-display text-white text-xl">{job.title}</h3>
            <div className="mt-1 text-slate-500 text-xs">
              {job.department} · {job.location}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="careers-apply-close"
            className="text-slate-400 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
              <Briefcase className="text-red-500" size={22} />
            </div>
            <h4 className="mt-4 font-display text-white text-2xl">Thanks for applying.</h4>
            <p className="mt-2 text-slate-400 text-sm">
              We've emailed you a confirmation. Our talent team will get back to you within
              5 working days.
            </p>
            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm px-5 py-2.5 rounded-sm"
              data-testid="careers-apply-done"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="overline mb-1.5 block">Full name *</label>
              <input
                required value={values.name} onChange={set("name")}
                data-testid="careers-apply-name"
                className="w-full bg-[#040812]/60 border border-white/10 focus:border-red-500 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Email *</label>
                <input
                  required type="email" value={values.email} onChange={set("email")}
                  data-testid="careers-apply-email"
                  className="w-full bg-[#040812]/60 border border-white/10 focus:border-red-500 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="overline mb-1.5 block">Phone</label>
                <input
                  value={values.phone} onChange={set("phone")}
                  data-testid="careers-apply-phone"
                  className="w-full bg-[#040812]/60 border border-white/10 focus:border-red-500 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="overline mb-1.5 block">Cover letter</label>
              <textarea
                rows={3} value={values.cover_letter} onChange={set("cover_letter")}
                data-testid="careers-apply-cover"
                className="w-full bg-[#040812]/60 border border-white/10 focus:border-red-500 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="overline mb-1.5 block">Resume (PDF/DOC · ≤10MB) *</label>
              <label
                htmlFor="resume-file"
                className="flex items-center gap-3 bg-[#040812]/60 border border-dashed border-white/15 hover:border-red-500 px-3 py-3 rounded-sm cursor-pointer"
              >
                <Upload size={16} className="text-red-500" />
                <span className="text-slate-300 text-sm truncate">
                  {file ? file.name : "Click to attach your resume"}
                </span>
              </label>
              <input
                id="resume-file" type="file"
                data-testid="careers-apply-resume"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="careers-apply-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-medium px-4 py-3 rounded-sm"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              Submit application
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/careers/jobs`);
      setJobs(data);
    } catch (e) {
      toast.error("Failed to load open roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <main data-testid="careers-page" className="min-h-screen bg-[#040812] pt-32 pb-24">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10">
        <span className="overline">/Careers</span>
        <h1 className="mt-5 font-display text-white text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[0.98]">
          Build things that <span className="text-red-500">actually protect</span> people.
        </h1>
        <p className="mt-6 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
          We're a small, obsessive team building satellite-connected security hardware and
          the software that makes it useful. If shipping matters to you more than titles,
          you'll fit right in.
        </p>
      </section>

      {/* Jobs */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-16">
        <div className="flex items-center justify-between">
          <div className="overline">Open roles</div>
          <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
            {loading ? "loading…" : `${jobs.length} open`}
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {loading && (
            <div className="col-span-full py-16 text-center">
              <Loader2 size={22} className="inline animate-spin text-slate-500" />
            </div>
          )}
          {!loading && jobs.length === 0 && (
            <div className="col-span-full py-16 text-center text-slate-500 text-sm border border-dashed border-white/10">
              No open roles at the moment. Check back soon — or send us a note at{" "}
              <a href="mailto:hr@srimansolutions.com" className="text-red-400">
                hr@srimansolutions.com
              </a>
              .
            </div>
          )}
          {jobs.map((j) => (
            <JobCard key={j.id} job={j} onApply={setApplying} />
          ))}
        </div>
      </section>

      {applying && (
        <ApplyModal job={applying} onClose={() => setApplying(null)} />
      )}
    </main>
  );
}
